import { useState } from 'react';
import { Button } from 'lift-ui';
import type { SyncUser } from '../../types';
import { TYPES, TYPE_LABEL, selectionByType, userKey, type Selection } from './shared';

const VISIBLE_LIMIT = 12;

interface SelectionSummaryProps {
  selection: Selection;
  onRemove: (users: SyncUser[]) => void;
}

export const SelectionSummary = ({ selection, onRemove }: SelectionSummaryProps) => {
  const [expanded, setExpanded] = useState(false);
  const grouped = selectionByType(selection);
  const total = Object.keys(selection).length;

  if (total === 0) {
    return <p className="text-sm text-muted-foreground">No one selected yet. Choose people or paste emails.</p>;
  }

  let budget = expanded ? Infinity : VISIBLE_LIMIT;

  return (
    <div className="flex flex-col gap-3" role="group" aria-label="Selected people">
      <p className="text-sm tabular-nums">
        {TYPES.filter((type) => grouped[type].length > 0)
          .map((type) => `${grouped[type].length} ${TYPE_LABEL[type]}`)
          .join(' · ')}
      </p>
      <div className="flex flex-wrap gap-2">
        {TYPES.flatMap((type) =>
          grouped[type].map((user) => {
            if (budget <= 0) return null;
            budget -= 1;
            return (
              <span
                key={userKey(user)}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted py-0.5 pl-3 pr-1 text-xs"
              >
                <span className="truncate">{user.email}</span>
                <button
                  type="button"
                  aria-label={`Remove ${user.email}`}
                  className="flex size-5 items-center justify-center rounded-full hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => onRemove([user])}
                >
                  <span aria-hidden>×</span>
                </button>
              </span>
            );
          })
        )}
        {!expanded && total > VISIBLE_LIMIT && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setExpanded(true)}>
            + {total - VISIBLE_LIMIT} more
          </Button>
        )}
        {expanded && total > VISIBLE_LIMIT && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setExpanded(false)}>
            Show fewer
          </Button>
        )}
      </div>
    </div>
  );
};
